<template>
	<v-container fluid class="pa-2 ma-0" dark>

		<!-- T-019: Loading overlay — shown while REST fetches are in-flight -->
		<v-overlay :value="loading" absolute z-index="10">
			<div class="text-center">
				<v-progress-circular indeterminate size="64" color="orange lighten-1"></v-progress-circular>
				<div class="mt-3 caption grey--text">{{ $ml.get('alarm_stats_loading') }}</div>
			</div>
		</v-overlay>

		<!-- T-018: Error state — full-width v-alert + retry button -->
		<v-alert
			v-if="error && !loading"
			type="error"
			prominent
			class="ma-2"
		>
			<v-row align="center" no-gutters>
				<v-col class="grow">{{ error }}</v-col>
				<v-col class="shrink">
					<v-btn small outlined color="white" class="ml-4" @click="_fetchAll">
						<v-icon small left>mdi-refresh</v-icon>
						{{ $ml.get('alarm_stats_retry') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-alert>

		<!-- Main dashboard content -->
		<template v-if="!loading && !error">

			<!-- Header row -->
			<v-row class="mb-2" align="center">
				<v-col cols="12" md="8">
					<div class="d-flex align-center">
						<v-btn icon small @click="$router.back()" class="mr-2">
							<v-icon small>mdi-arrow-left</v-icon>
						</v-btn>
						<span class="subtitle-1 font-weight-bold">
							{{ statsSnapshot.alarm_name || $ml.get('alarm_stats_title_fallback') }}
						</span>
						<v-chip x-small class="ml-2" color="primary">ID: {{ $route.params.id }}</v-chip>

						<!-- T-018: WebSocket connection indicator -->
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-icon
									x-small
									class="ml-3"
									:color="wsConnected ? 'green lighten-1' : 'red lighten-1'"
									v-on="on"
								>mdi-circle</v-icon>
							</template>
							<span>{{ wsConnected ? $ml.get('alarm_stats_realtime_connected') : $ml.get('alarm_stats_realtime_disconnected') }}</span>
						</v-tooltip>
					</div>
				</v-col>
				<v-col cols="12" md="4" class="d-flex justify-end align-center">
					<!-- T-017: Shift config button -->
					<v-btn small outlined color="orange darken-1" @click="shiftConfigOpen = true">
						<v-icon small left>mdi-cog-outline</v-icon>
						{{ $ml.get('alarm_stats_shift_button') }}
					</v-btn>
				</v-col>
			</v-row>

			<!-- KPI Cards row — T-006 (StatsKpiCards wired) -->
			<stats-kpi-cards
				:total-count="statsSnapshot.total_count || 0"
				:good-count="statsSnapshot.good_count || 0"
				:defect-count="statsSnapshot.defect_count || 0"
				:stoppages-count="stoppages.length"
				:total-idle-ms="totalIdleMs"
				:per-minute="statsSnapshot.current_rate || 0"
			></stats-kpi-cards>

			<!-- Middle row: Rate gauge + Shift Projection + OEE — T-010 -->
			<v-row class="mb-2">
				<!-- Rate gauge — T-007 -->
				<v-col cols="12" md="4">
					<stats-current-rate
						:current-rate="statsSnapshot.current_rate || 0"
						:target-rate="statsSnapshot.target_rate || 0"
						:peak-rate="statsSnapshot.peak_rate || 0"
						:avg-rate="statsSnapshot.avg_rate || 0"
					></stats-current-rate>
				</v-col>

				<!-- Shift projection — T-008 -->
				<v-col cols="12" md="4">
					<stats-shift-projection
						:upm="statsSnapshot.current_rate || 0"
						:total-counted="statsSnapshot.total_count || 0"
						:shift-elapsed-ms="statsSnapshot.shift_elapsed_ms || 0"
						:shift-duration-ms="statsSnapshot.shift_duration_ms || 0"
						:shift-config="shiftConfig"
					></stats-shift-projection>
				</v-col>

				<!-- OEE — T-009 -->
				<v-col cols="12" md="4">
					<stats-oee
						:availability="statsSnapshot.oee ? statsSnapshot.oee.availability : 0"
						:performance="statsSnapshot.oee ? statsSnapshot.oee.performance : 0"
						:quality="statsSnapshot.oee ? statsSnapshot.oee.quality : 0"
						:overall="statsSnapshot.oee ? statsSnapshot.oee.overall : 0"
					></stats-oee>
				</v-col>
			</v-row>

			<!-- Throughput chart — T-011 / T-013 -->
			<v-row class="mb-2">
				<v-col cols="12">
					<stats-throughput-chart
						:throughput-data="throughputSeries"
					></stats-throughput-chart>
				</v-col>
			</v-row>

			<!-- Stoppage log — T-012 / T-013 -->
			<v-row>
				<v-col cols="12">
					<stats-stoppage-log
						:stoppages="stoppages"
						:total-idle-ms="totalIdleMs"
						@update:note="handleStoppageNoteUpdate"
					></stats-stoppage-log>
				</v-col>
			</v-row>

		</template>

		<!-- T-017: Shift config dialog -->
		<stats-shift-config
			v-model="shiftConfigOpen"
			:config="shiftConfig"
			@save="handleShiftConfigSave"
		></stats-shift-config>

		<!-- T-018: Transient snackbar for operation feedback -->
		<v-snackbar
			v-model="snackbar.show"
			:color="snackbar.color"
			:timeout="snackbar.timeout"
			top
			right
		>
			{{ snackbar.message }}
			<v-btn
				small
				text
				color="white"
				@click="snackbar.show = false"
			>
				<v-icon small>mdi-close</v-icon>
			</v-btn>
		</v-snackbar>

	</v-container>
</template>

<script>
import io                     from 'socket.io-client';
import StatsKpiCards          from './StatsKpiCards.vue';
import StatsCurrentRate       from './StatsCurrentRate.vue';
import StatsShiftProjection   from './StatsShiftProjection.vue';
import StatsOee               from './StatsOee.vue';
import StatsThroughputChart   from './StatsThroughputChart.vue';
import StatsStoppageLog       from './StatsStoppageLog.vue';
import StatsShiftConfig       from './StatsShiftConfig.vue';

export default {
	name : 'AlarmStatsDashboard',

	components : {
		StatsKpiCards,
		StatsCurrentRate,
		StatsShiftProjection,
		StatsOee,
		StatsThroughputChart,
		StatsStoppageLog,
		StatsShiftConfig,
	},

	props : {
		id : {
			type    : [String, Number],
			default : null,
		},
	},

	data() {
		return {
			// ── Core state ──────────────────────────────────────────────
			loading          : false,
			error            : null,
			socket           : null,
			wsConnected      : false,

			// T-017: Shift config dialog visibility
			shiftConfigOpen  : false,

			// T-018: Snackbar state
			snackbar : {
				show    : false,
				message : '',
				color   : 'success',
				timeout : 4000,
			},

			// T-020: Stats defaults — guards against undefined WS payloads
			_statsDefaults : {
				total_count      : 0,
				good_count       : 0,
				defect_count     : 0,
				quality_pct      : 0,
				defect_rate      : 0,
				current_rate     : 0,
				rate_per_hour    : 0,
				peak_rate        : 0,
				avg_rate         : 0,
				stoppage_count   : 0,
				total_idle_time  : 0,
				is_stopped       : false,
				current_stoppage_duration: 0,
				oee              : 0,
				availability     : 0,
				performance      : 0,
				quality          : 0,
				projected_total  : 0,
				remaining_minutes: 0,
				shift_progress_pct: 0,
			},

			// ── Stats snapshot (populated by REST + WS in T-014/T-015) ──
			statsSnapshot : {
				alarm_id         : null,
				alarm_name       : '',
				total_count      : 0,
				good_count       : 0,
				defect_count     : 0,
				current_rate     : 0,
				target_rate      : 0,
				peak_rate        : 0,
				avg_rate         : 0,
				shift_elapsed_ms : 0,
				shift_duration_ms: 32400000, // 9 h default
				oee              : {
					availability : 0,
					performance  : 0,
					quality      : 0,
					overall      : 0,
				},
				last_updated     : null,
			},

			// ── Throughput series (T-011) ────────────────────────────────
			throughputSeries : [],

			// ── Stoppage log (T-012) ─────────────────────────────────────
			stoppages : [],

			// ── Shift config (T-016 / T-017) ────────────────────────────
			shiftConfig : {
				shift_start          : '08:00',
				shift_duration_min   : 540,
				target_rate          : 0,
				stoppage_threshold_ms: 5000,
			},
		};
	}, // data

	computed : {
		// Alarm ID as Number (route param is a string)
		alarmId() {
			return Number(this.$route.params.id || this.id);
		},

		// Total idle time in ms — from backend stats snapshot (authoritative)
		totalIdleMs() {
			return this.statsSnapshot.total_idle_time || 0;
		},

		// Shift progress as a percentage (0-100)
		shiftProgress() {
			const elapsed  = this.statsSnapshot.shift_elapsed_ms  || 0;
			const duration = this.statsSnapshot.shift_duration_ms || (this.shiftConfig.shift_duration_min || 540) * 60000;
			if (!duration) return 0;
			return Math.min(100, Math.round((elapsed / duration) * 100));
		},

		// Projected total for end of shift
		projectedTotal() {
			const upm        = this.statsSnapshot.current_rate    || 0;
			const counted    = this.statsSnapshot.total_count     || 0;
			const elapsed    = this.statsSnapshot.shift_elapsed_ms || 0;
			const duration   = this.statsSnapshot.shift_duration_ms || (this.shiftConfig.shift_duration_min || 540) * 60000;
			const remaining  = Math.max(0, duration - elapsed);
			const remainMin  = remaining / 60000;
			if (!upm) return counted;
			return Math.round((upm * remainMin) + counted);
		},

		// OEE color badge
		oeeColor() {
			const overall = this.statsSnapshot.oee ? this.statsSnapshot.oee.overall : 0;
			if (overall >= 85) return 'success';
			if (overall >= 60) return 'warning';
			return 'error';
		},
	}, // computed

	watch : {
		// T-015: React to route param change — re-fetch + reconnect socket
		'$route.params.id'(newId, oldId) {
			if (newId && newId !== oldId) {
				this._disconnectSocket();
				this._fetchAll();
			}
		},
	}, // watch

	mounted() {
		// T-017: Load shift config from localStorage before fetching data
		this._loadShiftConfigFromStorage();
		this._fetchAll();
	},

	beforeDestroy() {
		this._disconnectSocket();
	},

	methods : {

		// ─────────────────────────────────────────────────────────────────
		// T-014 — REST data fetching
		// ─────────────────────────────────────────────────────────────────

		async _fetchAll() {
			if (!this.alarmId) return;

			this.loading = true;
			this.error   = null;

			try {
				const baseUrl = this.$URL + '/api/v1/alarm/' + this.alarmId;

				const [statsRes, throughputRes, stoppagesRes] = await Promise.all([
					this.$axios.get(baseUrl + '/stats'),
					this.$axios.get(baseUrl + '/stats/throughput?granularity=minute&timeframe=shift'),
					this.$axios.get(baseUrl + '/stats/stoppages?limit=50'),
				]);

				// T-020: merge with defaults first to guard undefined fields
				const rawStats = (statsRes.data && typeof statsRes.data === 'object') ? statsRes.data : {};
				this.statsSnapshot    = { ...this.statsSnapshot, ...rawStats };
				this.throughputSeries = Array.isArray(throughputRes.data) ? throughputRes.data : [];
				this.stoppages        = Array.isArray(stoppagesRes.data)  ? stoppagesRes.data  : [];

			} catch (err) {
				const status = err && err.response && err.response.status;
				if (status === 404) {
					this.error = this.$ml.get('alarm_stats_not_found');
				} else {
					const msg = err && err.message ? err.message : this.$ml.get('alarm_stats_load_error');
					this.error = msg;
				}
				console.error('[AlarmStatsDashboard] _fetchAll error:', err);
			} finally {
				this.loading = false;
			}

			// T-015: Connect socket only if REST fetch succeeded (REQ-DS-001: 404 → no WS)
			if (!this.error) {
				this._connectSocket();
			}
		},

		// ─────────────────────────────────────────────────────────────────
		// T-015 — WebSocket real-time updates
		// ─────────────────────────────────────────────────────────────────

		_connectSocket() {
			// Tear down existing socket before creating a new one
			this._disconnectSocket();

			const socketUrl = this.$protocol_socket + this.$localsocket + ':' + this.$port_socket + '/workspace-data';

			this.socket = io(socketUrl, {
				transports         : ['websocket'],
				reconnection       : true,
				reconnectionDelay  : 2000,
				reconnectionAttempts: 10,
			});

			this.socket.on('connect', () => {
				this.wsConnected = true;
			});

			// T-018: Show transient snackbar on WS disconnect
			this.socket.on('disconnect', () => {
				this.wsConnected = false;
				this._showSnackbar(this.$ml.get('alarm_stats_ws_lost'), 'warning', 6000);
			});

			this.socket.on('reconnect', () => {
				this.wsConnected = true;
				this._showSnackbar(this.$ml.get('alarm_stats_ws_reconnected'), 'success', 3000);
			});

			// ── alarm_stats — snapshot refresh ──────────────────────────
			// T-020: wrapped in try/catch — skip malformed payloads silently
			this.socket.on('alarm_stats', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;
					// Merge with defaults to ensure all fields exist
					this.statsSnapshot = {
						...this._statsDefaults,
						...this.statsSnapshot,
						...payload,
						// Ensure nested oee object is always valid
						oee: payload.oee && typeof payload.oee === 'object'
							? payload.oee
							: (this.statsSnapshot.oee || { availability: 0, performance: 0, quality: 0, overall: 0 }),
					};
				} catch (e) {
					console.warn('[AlarmStatsDashboard] alarm_stats payload error:', e);
				}
			});

			// ── alarm_throughput — live chart refresh ───────────────────
			// Periodic snapshot (every 5s) of the per-minute buckets. Keeps the
			// throughput chart moving in real time instead of only on page load.
			this.socket.on('alarm_throughput', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;
					if (!Array.isArray(payload.buckets)) return;
					this.throughputSeries = payload.buckets;
				} catch (e) {
					console.warn('[AlarmStatsDashboard] alarm_throughput payload error:', e);
				}
			});

			// ── alarm_stoppage — live stoppage push/close ────────────────
			// T-020: wrapped in try/catch — skip malformed payloads silently
			this.socket.on('alarm_stoppage', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;

					if (payload.type === 'stoppage_start') {
						if (!payload.stoppage || typeof payload.stoppage !== 'object') return;
						const newStoppage = {
							...payload.stoppage,
							device_id : payload.device_id,
						};
						this.stoppages = [newStoppage, ...this.stoppages];

					} else if (payload.type === 'stoppage_end') {
						if (!payload.stoppage || typeof payload.stoppage !== 'object') return;
						const idx = this.stoppages.findIndex(s => s.id === payload.stoppage.id);
						if (idx >= 0) {
							this.$set(this.stoppages, idx, {
								...this.stoppages[idx],
								ended_at    : payload.stoppage.ended_at,
								duration_ms : payload.stoppage.duration_ms,
								rate_after  : payload.stoppage.rate_after,
								status      : 'RESOLVED',
							});
						}
					}
				} catch (e) {
					console.warn('[AlarmStatsDashboard] alarm_stoppage payload error:', e);
				}
			});
		},

		_disconnectSocket() {
			if (this.socket) {
				this.socket.off('alarm_stats');
				this.socket.off('alarm_throughput');
				this.socket.off('alarm_stoppage');
				this.socket.disconnect();
				this.socket = null;
			}
			this.wsConnected = false;
		},

		// ─────────────────────────────────────────────────────────────────
		// T-014 — PUT note update
		// ─────────────────────────────────────────────────────────────────

		/**
		 * Handle note update emitted by StatsStoppageLog.
		 * Payload: { stoppageId, note }
		 */
		handleStoppageNoteUpdate({ stoppageId, note }) {
			const url = this.$URL + '/api/v1/alarm/' + this.alarmId + '/stats/stoppages/' + stoppageId;
			this.$axios.put(url, { note }).catch(err => {
				console.error('[AlarmStatsDashboard] PUT note error:', err);
				// T-018: surface note save errors as snackbar
				this._showSnackbar(this.$ml.get('alarm_stats_note_save_error'), 'error');
			});
		},

		// ─────────────────────────────────────────────────────────────────
		// T-017 — Shift config: localStorage persistence + API save
		// ─────────────────────────────────────────────────────────────────

		_loadShiftConfigFromStorage() {
			try {
				const key  = 'alarm-shift-config-' + this.alarmId;
				const raw  = localStorage.getItem(key);
				if (raw) {
					const parsed = JSON.parse(raw);
					if (parsed && typeof parsed === 'object') {
						this.shiftConfig = {
							shift_start          : parsed.shift_start           || '08:00',
							shift_duration_min   : parsed.shift_duration_min    != null ? parsed.shift_duration_min    : 540,
							target_rate          : parsed.target_rate           != null ? parsed.target_rate           : 0,
							stoppage_threshold_ms: parsed.stoppage_threshold_ms != null ? parsed.stoppage_threshold_ms : 5000,
						};
					}
				}
			} catch (e) {
				console.warn('[AlarmStatsDashboard] Could not load shift config from localStorage:', e);
			}
		},

		async handleShiftConfigSave(config) {
			// 1. Persist to backend
			const url = this.$URL + '/api/v1/alarm/' + this.alarmId + '/shift-config';
			try {
				await this.$axios.put(url, config);
			} catch (err) {
				console.error('[AlarmStatsDashboard] PUT shift-config error:', err);
				this._showSnackbar(this.$ml.get('alarm_stats_config_save_error'), 'error');
				// Still save locally — don't block the user
			}

			// 2. Persist to localStorage regardless of API result
			try {
				const key = 'alarm-shift-config-' + this.alarmId;
				localStorage.setItem(key, JSON.stringify(config));
			} catch (e) {
				console.warn('[AlarmStatsDashboard] Could not save shift config to localStorage:', e);
			}

			// 3. Update local state
			this.shiftConfig = { ...config };

			// 4. Show success feedback
			this._showSnackbar(this.$ml.get('alarm_stats_config_saved'), 'success');
		},

		// ─────────────────────────────────────────────────────────────────
		// T-018 — Snackbar helper
		// ─────────────────────────────────────────────────────────────────

		_showSnackbar(message, color = 'info', timeout = 4000) {
			this.snackbar.message = message;
			this.snackbar.color   = color;
			this.snackbar.timeout = timeout;
			this.snackbar.show    = true;
		},

		// ─────────────────────────────────────────────────────────────────
		// Utility helpers
		// ─────────────────────────────────────────────────────────────────

		formatDate(ts) {
			if (!ts) return '—';
			try {
				const m = this.$momentz.tz(ts, this.$timezone);
				return m.isValid() ? m.format('M/D/YYYY, h:mm A') : String(ts);
			} catch (e) {
				return ts;
			}
		},

		formatDuration(ms) {
			if (!ms || ms <= 0) return '—';
			const totalSeconds = Math.floor(ms / 1000);
			const hours        = Math.floor(totalSeconds / 3600);
			const minutes      = Math.floor((totalSeconds % 3600) / 60);
			const seconds      = totalSeconds % 60;
			if (hours > 0) return `${hours}h ${minutes}m`;
			if (minutes > 0) return `${minutes}m ${seconds}s`;
			return `${seconds}s`;
		},

	}, // methods
};
</script>
